import { Badge, Box, Button, Flex, Text } from '@chakra-ui/react'
import { Icon } from '@chakra-ui/icons'
import React, { useCallback, useRef, useState } from 'react'
import { FaPause, FaPlay } from 'react-icons/fa6'
import { useDisclosure } from '@chakra-ui/react'
import ActionConfirmation from '~components/shared/modals/action-confirmation'
import { useActionItems } from '~contexts/action-item-provider'
import {
    ActionItemFieldsFragment,
    ActionItemStatus,
    namedOperations,
    useUpdateActionItemMutation,
} from '~graphql/generated/graphql'
import {
    getOfficeTaskPriorityColor,
    parseOfficeTaskDescription,
} from './office-task.helpers'

interface Props {
    actionItem: ActionItemFieldsFragment
    isHistorical?: boolean
    isSmall?: boolean
}

export default function OfficeTaskActions({
    actionItem,
    isHistorical,
    isSmall = false,
}: Props) {
    const { refetch } = useActionItems()
    const { isOpen, onOpen, onClose } = useDisclosure()
    const parsedOfficeTask = parseOfficeTaskDescription(actionItem.description)

    const recordingUrl = actionItem.originating_call?.recording_url
    const audioRef = useRef<HTMLAudioElement | null>(null)
    const [isPlaying, setIsPlaying] = useState(false)

    const togglePlayback = useCallback(() => {
        const audio = audioRef.current
        if (!audio) return

        if (isPlaying) {
            audio.pause()
        } else {
            audio.play()
        }
        setIsPlaying(!isPlaying)
    }, [isPlaying])

    const [updateActionItem, { loading: updateLoading }] =
        useUpdateActionItemMutation({
            variables: {
                id: actionItem.id,
                data: {
                    status: ActionItemStatus.Completed,
                },
            },
            refetchQueries: [
                namedOperations.Query.countActionItems,
                namedOperations.Query.queryActionItems,
            ],
            onCompleted: () => refetch(),
            awaitRefetchQueries: true,
        })

    async function handleUpdate() {
        await updateActionItem()
    }

    if (isHistorical) {
        return <Box display="none" />
    }

    return (
        <>
            <Flex
                gap={2}
                flexDirection={{ base: 'row-reverse', md: 'column' }}
                w={{ base: 'full', md: 'max-content' }}
            >
                {recordingUrl && (
                    <>
                        <audio
                            ref={audioRef}
                            src={recordingUrl}
                            preload="none"
                            onEnded={() => setIsPlaying(false)}
                            onPause={() => setIsPlaying(false)}
                            onPlay={() => setIsPlaying(true)}
                        />
                        <Button
                            rightIcon={
                                <Icon as={isPlaying ? FaPause : FaPlay} />
                            }
                            size={isSmall ? 'xs' : 'sm'}
                            variant="outline"
                            colorScheme="brand"
                            onClick={togglePlayback}
                        >
                            Call Recording
                        </Button>
                    </>
                )}
                <Button
                    size={isSmall ? 'xs' : 'sm'}
                    colorScheme="brand"
                    shadow="sm"
                    onClick={onOpen}
                >
                    Mark as complete
                </Button>
            </Flex>
            <ActionConfirmation
                isOpen={isOpen}
                onClose={onClose}
                onConfirm={handleUpdate}
                isLoading={updateLoading}
                title="Mark office task as completed?"
                size="xl"
            >
                <Box>
                    <Text fontWeight="semibold" mb={1}>
                        Are you sure you want to mark this office task as
                        completed?
                    </Text>
                    <Text fontSize="sm" mb={4}>
                        This removes it from your pending action list.
                    </Text>
                    <Box mt={4} p={3} bg="gray.50" borderRadius="md">
                        <Text fontSize="sm">
                            <Box as="span" fontWeight="medium">
                                Request Type:
                            </Box>{' '}
                            {parsedOfficeTask?.requestTypeLabel ||
                                'Office Task'}
                        </Text>
                        {!!parsedOfficeTask?.priority && (
                            <Box mt={2}>
                                <Badge
                                    variant="subtle"
                                    colorScheme={getOfficeTaskPriorityColor(
                                        parsedOfficeTask.priority,
                                    )}
                                    textTransform="capitalize"
                                >
                                    {parsedOfficeTask.priorityLabel} Priority
                                </Badge>
                            </Box>
                        )}
                        <Text fontSize="sm" mt={3} whiteSpace="pre-wrap">
                            {parsedOfficeTask?.details ||
                                actionItem.description ||
                                'No details provided'}
                        </Text>
                    </Box>
                </Box>
            </ActionConfirmation>
        </>
    )
}
